<template>
  <div class="page-container">
    <!-- Left-side Navigation Bar -->
    <div class="nav-bar">
      <navigator />
    </div>

    <!-- Main Content Container -->
    <div class="main-content">
      <!-- Users Header -->
      <div class="header">
        <h1>Users</h1>
      </div>

      <!-- Content with Filters on the Left, User Cards on the Right -->
      <div class="page-background">
        <!-- Filters Box (White background) -->
        <div class="filters-container">
          <div class="filter-title">
            <h2>Discover Users</h2>
          </div>

          <!-- Search Section -->
          <div class="search-section">
            <b-form-input
              v-model="searchTerm"
              :placeholder="'Search by ' + getSearchTypeLabel()"
              class="text-input"
              :style="{ backgroundColor: '#f1f1f1', color: 'black' }"
            ></b-form-input>

            <b-dropdown
              text="Search by"
              color="white"
              variant="outline-warning"
              class="text-input search-type-dropdown"
              size="sm"
            >
              <b-dropdown-item
                v-for="option in searchOptions"
                :key="option.value"
                @click="selectedSearchType = option.value"
              >
                {{ option.label }}
              </b-dropdown-item>
            </b-dropdown>
          </div>

          <!-- General Filter for User Type and Gender -->
          <div class="filter-tags">
            <b-dropdown
              text="Filter by"
              color="white"
              variant="outline-warning"
              class="text-input filter-dropdown"
              size="sm"
            >
              <!-- User Type Filters -->
              <b-dropdown-item @click="filterBy('userType', 'Tester')"
                >Tester</b-dropdown-item
              >
              <b-dropdown-item @click="filterBy('userType', 'Developer')"
                >Developer</b-dropdown-item
              >
            </b-dropdown>
          </div>
          <b-button
            variant="outline-danger"
            class="clear-filters-button"
            size="sm"
            @click="clearFilters"
          >
            Clear All Filters
          </b-button>
        </div>

        <!-- User Cards Section (Right side of filters) -->
        <div class="user-cards-container">
          <div class="bulk-actions">
            <b-form-checkbox
              class="select-all-checkbox"
              :checked="allOnPageSelected"
              :indeterminate="someOnPageSelected"
              @change="toggleSelectAllOnPage"
            >
              Select all on this page
            </b-form-checkbox>
            <span class="selected-count">
              {{ selectedUserIds.length }} selected
              <span v-if="offPageSelectedCount" class="off-page-note">
                ({{ offPageSelectedCount }} on other pages)
              </span>
            </span>
            <b-button
              v-if="selectedUserIds.length"
              class="clear-selection-button"
              size="sm"
              variant="link"
              @click="clearSelection"
            >
              Clear selection
            </b-button>
            <b-button
              class="bulk-delete-button"
              size="sm"
              :disabled="selectedUserIds.length === 0"
              v-b-modal.bulk-delete-modal
            >
              Delete Selected
            </b-button>
          </div>

          <b-modal id="bulk-delete-modal" title="Confirm Deletion" @ok="removeSelectedUsers">
            <template #modal-header="{ close }">
              <h5 class="modal-title text-danger">Confirm Deletion</h5>
              <b-button variant="outline-danger" size="sm" @click="close">X</b-button>
            </template>
            <p>
              Are you sure you want to delete
              {{ selectedUserIds.length }}
              selected {{ selectedUserIds.length === 1 ? "user" : "users" }}?
            </p>
            <template #modal-footer="{ cancel, ok }">
              <b-button variant="danger" @click="ok">Delete</b-button>
              <b-button variant="outline-secondary" @click="cancel">Cancel</b-button>
            </template>
          </b-modal>

          <div id="user-cards" class="card-grid">
            <UserItem
              v-for="user in filteredUsers"
              :key="user.id"
              :userID="user.id"
              :userName="user.userName"
              :email="user.email"
              :userType="user.userType"
              :nickName="user.nickName"
              :bio="user.bio"
              :gender="user.gender"
              :games="user.games"
              :selected="selectedUserIds.includes(user.id)"
              @toggleSelect="toggleSelect"
              @messageUser="startConversationWith"
              @saveUser="updateUserInFirestore"
              v-on:removeUser="removeUser"
            />
          </div>

          <!-- Pagination -->
          <div class="pagination-container">
  <!-- Previous Button -->
  <b-button
    class="pagination-arrow"
    @click="changePage(currentPage - 1)"
    :disabled="currentPage === 1"
  >
    &lt;
  </b-button>

  <!-- Page Numbers -->
  <span
    v-for="page in totalPages"
    :key="page"
    class="pagination-circle"
    :class="{ active: page === currentPage }"
    @click="changePage(page)"
  >
    {{ page }}
  </span>

  <!-- Next Button -->
  <b-button
    class="pagination-arrow"
    @click="changePage(currentPage + 1)"
    :disabled="currentPage === totalPages"
  >
    &gt;
  </b-button>
</div>
        </div>
      </div>
    </div>

    <MessagePanel ref="messagePanel" :contacts="contacts" :userType="userType" />
  </div>
</template>

<script>
import navigator from "../layout/NavBarNew.vue";
import UserItem from "./UserItem.vue";
import Loading from "../loading/index.vue";
import UserCollection from "../../Firestore/UserCollection";
import UserObject from "../../Firestore/UserObject";
import Chat from "../../Firestore/Chat.js";
import MessagePanel from "../messagebox/developer-messagepanel.vue";

export default {
  name: "Users",
  components: {
    UserItem,
    navigator,
    Loading,
    MessagePanel
  },
  data() {
    return {
      navFlag: 2,
      userType: window.localStorage.getItem("userType")
        ? parseInt(window.localStorage.getItem("userType"))
        : -1, // 0: Tester, 1: Company, 2: Admin
      isLoading: false,
      currentPage: 1,
      usersPerPage: 8, // Adjusted to 16 users per page
      allUsers: [],
      searchTerm: "",
      searchOptions: [
        { value: "userName", label: "Username" },
        { value: "email", label: "Email" }
      ],
      selectedSearchType: "userName", // Default search type
      selectedFilterType: null, // Keeps track of whether we're filtering by userType or gender
      selectedFilterValue: null, // Stores the specific filter value (e.g., Tester, Male)
      selectedUserIds: [],
      userId: window.localStorage.getItem("userId"),
      contacts: [],
      conversations: [],
      conversationsUnsub: null
    };
  },
  computed: {
    filteredUsers() {
      let filteredUsers = this.allUsers;

      if (this.searchTerm) {
        filteredUsers = filteredUsers.filter(user => {
          return (user[this.selectedSearchType] || "")
            .toString()
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase());
        });
      }

      if (this.selectedFilterType && this.selectedFilterValue) {
        filteredUsers = filteredUsers.filter(
          user =>
            user[this.selectedFilterType] &&
            user[this.selectedFilterType].toLowerCase() ===
              this.selectedFilterValue.toLowerCase()
        );
      }

      filteredUsers = filteredUsers.slice().sort(
        (a, b) =>
          (a.userType || "").localeCompare(b.userType || "", undefined, {
            sensitivity: "base"
          }) ||
          (a.userName || "").localeCompare(b.userName || "", undefined, {
            sensitivity: "base"
          })
      );

      const start = (this.currentPage - 1) * this.usersPerPage;
      const end = start + this.usersPerPage;
      return filteredUsers.slice(start, end);
    },

    totalPages() {
      const filteredLength = this.allUsers.filter(user => {
        let matchesSearch = this.searchTerm
          ? (user[this.selectedSearchType] || "")
              .toString()
              .toLowerCase()
              .includes(this.searchTerm.toLowerCase())
          : true;
        let matchesFilter =
          this.selectedFilterType && this.selectedFilterValue
            ? user[this.selectedFilterType] &&
              user[this.selectedFilterType].toLowerCase() ===
                this.selectedFilterValue.toLowerCase()
            : true;
        return matchesSearch && matchesFilter;
      }).length;

      return Math.ceil(filteredLength / this.usersPerPage);
    },

    allOnPageSelected() {
      return (
        this.filteredUsers.length > 0 &&
        this.filteredUsers.every(user => this.selectedUserIds.includes(user.id))
      );
    },

    someOnPageSelected() {
      return (
        !this.allOnPageSelected &&
        this.filteredUsers.some(user => this.selectedUserIds.includes(user.id))
      );
    },

    offPageSelectedCount() {
      const pageIds = this.filteredUsers.map(user => user.id);
      return this.selectedUserIds.filter(id => !pageIds.includes(id)).length;
    }
  },
  mounted() {
    if (window.localStorage.getItem("userType")) {
      this.userType = parseInt(window.localStorage.getItem("userType"));
      this.getUsers();
      this.loadConversations();
    } else {
      this.$router.push({ name: "login" });
    }
  },
  beforeDestroy() {
    if (this.conversationsUnsub) {
      this.conversationsUnsub();
      this.conversationsUnsub = null;
    }
  },
  methods: {
    loadConversations() {
      Chat.getUserConversations(this.userId, result => {
        if (result.success) {
          this.conversations = result.data.filter(c => c.isAdminChat !== true);
          this.transformConversationsToContacts();
        }
      })
        .then(res => {
          if (res && res.unsubscribe) this.conversationsUnsub = res.unsubscribe;
        })
        .catch(error => console.error("Error loading conversations:", error));
    },
    transformConversationsToContacts() {
      const currentUserId = this.userId;
      const contactsArray = [];

      const promises = this.conversations.map(conversation => {
        const otherUserId = (conversation.participants || []).filter(id => id !== currentUserId)[0];
        if (!otherUserId) return Promise.resolve();

        return Chat.getUserInfo(otherUserId)
          .then(userResult => {
            if (!userResult.success) return;
            const userData = userResult.data;
            contactsArray.push({
              id: otherUserId,
              conversationId: conversation.conversationId,
              type: "user",
              name: userData.userName || userData.nickName || userData.email || "Unknown User",
              avatar: userData.img || require("../../assets/avator_example.jpg"),
              lastMessage: conversation.lastMessage || "No messages yet",
              lastMessageAt: conversation.lastMessageAt,
              unreadCount: (conversation.unreadCount && conversation.unreadCount[currentUserId]) || 0
            });
          })
          .catch(() => {});
      });

      Promise.all(promises).then(() => {
        const getTime = ts => {
          if (!ts) return 0;
          if (ts.toDate) return ts.toDate().getTime();
          if (ts instanceof Date) return ts.getTime();
          return 0;
        };
        contactsArray.sort((a, b) => getTime(b.lastMessageAt) - getTime(a.lastMessageAt));
        this.contacts = contactsArray;
      });
    },
    startConversationWith(userID) {
      const user = this.allUsers.find(u => u.id === userID);
      if (!user) return;

      Chat.getOrCreateConversation(this.userId, userID)
        .then(response => {
          const conversation = response.data;
          const openChat = () => {
            this.$refs.messagePanel.openChatWithContact({
              id: userID,
              conversationId: conversation.conversationId,
              type: "user",
              name: user.userName || user.nickName || user.email || "Unknown User",
              avatar: require("../../assets/avator_example.jpg"),
              lastMessage: conversation.lastMessage || "No messages yet",
              lastMessageAt: conversation.lastMessageAt || new Date(),
              unreadCount: 0
            });
          };

          if (conversation.acceptedStatus !== "accepted") {
            Chat.acceptConversation(conversation.conversationId).then(openChat);
          } else {
            openChat();
          }
        })
        .catch(error => {
          console.error("Error starting conversation:", error);
          this.$snackbar.showSnackbar("Could not start conversation", "#d83b3b");
        });
    },
    updateUserInFirestore(updatedUser) {
  const { userID, ...fieldsToUpdate } = updatedUser;

  // Log the fields to update for debugging
  console.log("Updating user in Firestore:", fieldsToUpdate);

  // Update Firestore
  UserCollection.updateUser(userID, fieldsToUpdate)
    .then(() => {
      console.log("User updated successfully!");

      // Refresh Firestore data
      this.getUsers();

      // Update the user in the local `allUsers` array for immediate UI changes
      const userIndex = this.allUsers.findIndex(user => user.id === userID);
      if (userIndex !== -1) {
        this.$set(this.allUsers, userIndex, {
          ...this.allUsers[userIndex],
          ...fieldsToUpdate,
        });
      }
    })
    .catch(error => {
      console.error("Error updating user:", error);
    });
},


    getUsers() {
      this.isLoading = true;
      UserCollection.getAllUsers()
        .then(res => {
          this.allUsers = res.data;
          this.selectedUserIds = this.selectedUserIds.filter(id =>
            res.data.some(user => user.id === id)
          );
          this.isLoading = false;
        })
        .catch(error => {
          console.log(error);
          this.isLoading = false;
        });
    },
    removeUser(userID) {
      UserObject.deleteUser(userID)
      .then(response => {
        if (response.success) {
          console.log("User deleted successfully ");
          this.getUsers(); // Refresh user list
        } else {
          console.error("Error deleting user:", response.error);
          alert(response.error || "Unknown error");
        }
    })
    .catch(error => {
      console.error("Error in deleteUser:", error);
      alert("Unexpected error. Please try again.");
    });
    },
    toggleSelect(userID) {
      const index = this.selectedUserIds.indexOf(userID);
      if (index === -1) {
        this.selectedUserIds.push(userID);
      } else {
        this.selectedUserIds.splice(index, 1);
      }
    },
    clearSelection() {
      this.selectedUserIds = [];
    },
    toggleSelectAllOnPage(checked) {
      const pageIds = this.filteredUsers.map(user => user.id);
      if (checked) {
        pageIds.forEach(id => {
          if (!this.selectedUserIds.includes(id)) {
            this.selectedUserIds.push(id);
          }
        });
      } else {
        this.selectedUserIds = this.selectedUserIds.filter(
          id => !pageIds.includes(id)
        );
      }
    },
    removeSelectedUsers() {
      const idsToDelete = this.selectedUserIds.slice();
      Promise.all(
        idsToDelete.map(id =>
          UserObject.deleteUser(id)
            .then(response => response.success)
            .catch(error => {
              console.error("Error deleting user:", id, error);
              return false;
            })
        )
      ).then(results => {
        const failed = results.filter(ok => !ok).length;
        this.selectedUserIds = [];
        this.getUsers();
        if (failed) {
          this.$snackbar.showSnackbar(
            `${failed} of ${idsToDelete.length} users could not be deleted`,
            "#d83b3b"
          );
        } else {
          this.$snackbar.showSnackbar(
            `${idsToDelete.length} ${idsToDelete.length === 1 ? "user" : "users"} deleted`,
            "#EED202"
          );
        }
      });
    },
    filterBy(type, value) {
      this.selectedFilterType = type;
      this.selectedFilterValue = value;
      if (this.currentPage !== 1) {
        this.currentPage = 1; // Reset to page 1 if filter is changed
      }
      //this.$snackbar.showSnackbar("Filtering by:" +{value}, "#EED202");
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    getSearchTypeLabel() {
      const selected = this.searchOptions.find(
        option => option.value === this.selectedSearchType
      );
      return selected ? selected.label : "Search";
    },
    clearFilters() {
      this.selectedFilterType = null;
      this.selectedFilterValue = null;
      this.searchTerm = "";
      this.currentPage = 1; // Reset to first page after clearing filters
      this.$snackbar.showSnackbar("Filters Cleared!", "#EED202");
    }
  },
  watch: {
    userName(newVal) {
      this.localUserName = newVal;
    },
    email(newVal) {
      this.localEmail = newVal;
    },
    userType(newVal) {
      this.localUserType = newVal;
    },
    nickName(newVal) {
      this.localNickName = newVal;
    },
    bio(newVal) {
      this.localBio = newVal;
    },
    gender(newVal) {
      this.localGender = newVal;
    },
    searchTerm() {
      this.currentPage = 1;
    },
    selectedSearchType() {
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px; /* Add space between items */
  margin-top: 20px; /* Space from the rest of the content */
}

.pagination-circle {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: #f1f1f1; /* Default background */
  color: #333; /* Default text color */
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  cursor: pointer;
  border: 1px solid #ddd;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.pagination-circle.active {
  background-color: #f97427; /* Active background color (blue) */
  color: white; /* Active text color */
  border-color: rgba(0, 0, 0, 0.4); /* Slightly darker border for active */
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3); 
}

.pagination-circle:hover:not(.active) {
  background-color: #e9ecef; /* Light gray hover effect */
  border-color: rgba(0, 0, 0, 0.3);
}

.pagination-arrow {
  width: 35px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  font-size: 1rem;
  border: 1px solid #ddd;
  background-color: #f1f1f1;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.pagination-arrow:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: rgba(0, 0, 0, 0.3);
}

.pagination-arrow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-container {
  display: flex;
  gap: 20px;
  background-color: #f0f0f0; /* Light grey background for the whole page */
  min-height: 100vh;
  min-width: 100%;
  width: 100%;
  overflow-x: hidden;
}

.nav-bar {
  width: 220px;
  /* Adjust the width for the navbar */
}

.main-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.header {
  width: 100vw; /* Ensure the header takes full width */
  display: flex;
  margin-bottom: 30px;
  justify-content: center; /* Center the text horizontally */
  align-items: center; 
  text-align: center;
  padding-top: 10px;
  height: 80px;
  box-shadow: 0 4px 10px -6px grey;
  background-color: white; 
  
}

.header h1 {
  background-color: rgb(255, 255, 255);
  color: #ff7222;
  font-weight: bold;
  height: 80px;
  width: 100vw;
}

.page-background {
  display: flex;
  gap: 20px;
  width: 100%;
}

.filters-container {
  background-color: white; /* White background for the filter section */
  padding: 20px;
  border-radius: 30px;
  margin-left: 25px;
  width: 350px; /* Set width for the filter box */
  height: 600px;
  margin-bottom: 10px;
}

.filter-title h2 {
  margin-bottom: 20px;
  color: rgb(2, 2, 2); /* Set text color to black */
}

.search-section {
  margin-top: 20px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px; /* Adds space between search bar and dropdown */
}

.filter-dropdown {
  width: 100%; /* Full width like the search dropdown */
  background-color: #f97427; /* Orange background */
  color: white !important; /* White text */
  border: 1px solid #f97427; /* Border matches background color */
}

.filter-dropdown .dropdown-item {
  background-color: #f97427; /* Orange for dropdown items */
  color: white !important; /* White text for dropdown items */
}

.filter-dropdown .dropdown-item:hover {
  background-color: #e0631f; /* Darker orange on hover */
}

.search-type-dropdown,
.user-type-dropdown {
  margin-top: 20px;
  width: 100%;
  background-color: #f97427; /* Make the dropdown button orange */
  color: white !important; /* Set text color to white */
  border: 1px solid #f97427; /* Border matches the button color */
}

.search-type-dropdown .dropdown-item,
.user-type-dropdown .dropdown-item {
  background-color: #f97427; /* Ensure dropdown items have same background */
  color: white !important;
}

.filter-dropdown >>> .dropdown-toggle,
.search-type-dropdown >>> .dropdown-toggle {
  background-color: #f97427;
  border-color: #f97427;
  color: white !important; /* Set text color to white */
}

.filter-dropdown >>> .dropdown-toggle:hover,
.search-type-dropdown >>> .dropdown-toggle:hover {
  background-color: #e0631f;
  border-color: #e0631f;
  color: white; /* Keep text white on hover */
}

.search-type-dropdown .dropdown-item:hover,
.user-type-dropdown .dropdown-item:hover {
  background-color: #e0631f; /* Hover color */
}

.filter-tags {
  margin-top: 10px;
  margin-bottom: 20px;
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 10px;
  padding: 6px 12px;
  background-color: white;
  border-radius: 0.8rem;
}

.select-all-checkbox >>> input {
  margin-right: 6px;
}

.selected-count {
  color: #333;
  font-size: 0.9rem;
}

.off-page-note {
  color: #6c757d;
}

.clear-selection-button {
  color: #f97427;
  font-size: 0.9rem;
  padding: 0;
  text-decoration: underline;
}

.clear-selection-button:hover {
  color: #d45f1e;
}

.bulk-delete-button {
  background-color: #d83b3b;
  border: 1px solid #d83b3b;
  color: white;
  border-radius: 5px;
  margin-left: auto;
}

.bulk-delete-button:hover:not(:disabled) {
  background-color: #c50c0c;
  border: 1px solid #c50c0c;
}

.bulk-delete-button:disabled {
  background-color: #b8b8b8;
  border: 1px solid #b8b8b8;
  opacity: 1;
}

.modal-header button {
  margin-left: auto;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 129px;
  grid-gap: 0;
  margin: 0;
  gap: 0px;
  width: 100%;
  min-height: 516px;
}

.pagination-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-button {
  background-color: #ff9900; /* Pagination button color */
  color: white;
  border-radius: 5px;
  margin: 0 10px;
  width: 90px;
}

.pagination-button:hover {
  background-color: #e68a00;
}

.pagination-info {
  color: #333;
  font-size: 1.2rem;
  padding: 20px;
}
.clear-filters-button {
  margin-top: 10px;
  background-color: #d8d2d2; /* Light red for clear button */
  color: black;
  text-align: center;
  border: 1px solid #d8d2d2;
  border-radius: 5px;
}
.clear-filters-button:hover {
  background-color: #999292; /* Darker red on hover */
  border: 1px solid #999292;

}
</style>


// WEBPACK FOOTER //
// src/components/admin-only/Users.vue